/**
 * Fun Arcade tutorials, shown the first time a level with that id opens.
 * The first levels teach one control each; every chapter's first level
 * introduces its new idea. `focus` tells the scene (machine, wish card,
 * marquee grips, chute, a knock, heavy plushies, ribbons, glass walls,
 * bouncy balls, tall piles) or the HUD (undo, target, hint) what to
 * highlight; "demo" keeps a sparkle on the column of the next good coin
 * until the player spends it there.
 */
export type ClawTutorialFocus =
  | "machine"
  | "wish"
  | "grips"
  | "chute"
  | "knock"
  | "demo"
  | "undo"
  | "target"
  | "hint"
  | "tall"
  | "heavy"
  | "ribbon"
  | "glass"
  | "bouncy";

export interface ClawTutorialStep {
  readonly text: string;
  readonly focus?: ClawTutorialFocus;
}

export interface ClawTutorial {
  readonly id: string;
  readonly steps: readonly ClawTutorialStep[];
}

export const CLAW_TUTORIALS: readonly ClawTutorial[] = [
  {
    id: "claw-intro",
    steps: [
      { text: "Chào mừng tới Nhà Vui Nhộn! Máy gắp thú ở đây lém lỉnh lắm, phải tính khéo mới rinh được quà nha.", focus: "machine" },
      { text: "Phiếu ước ghi các bạn thú bé đang mong. Bạn nào đeo vé vàng thì đưa xuống lỗ nhận quà bên trái là xong.", focus: "wish" },
      { text: "Bảng đèn trên nóc là sức cẩu. Cẩu yếu tay, kéo đủ số cột trên đèn là tuột, ở máy này là hai cột.", focus: "grips" },
      { text: "Chạm vào một cột để thả xu. Rê tay qua các cột là thấy trước thú sẽ rơi về đâu. Thử cột có đốm sáng nè.", focus: "demo" },
    ],
  },
  {
    id: "claw-knock",
    steps: [
      { text: "Thú tuột tay mà rơi trúng đầu bạn khác sẽ hất bạn đó văng sang trái một cột. Hất trúng lỗ là có quà luôn, khỏi tốn xu!", focus: "knock" },
    ],
  },
  {
    id: "claw-target",
    steps: [
      { text: "Số bên cạnh hồng tâm là số xu tối đa để được ba sao. Lời giải gọn nhất chỉ cần ít hơn một xu thôi.", focus: "target" },
      { text: "Lỡ tay thì hoàn tác thoải mái, không tốn gì hết. Sao chỉ tính các xu còn giữ lại lúc rinh đủ quà.", focus: "undo" },
    ],
  },
  {
    id: "claw-hint",
    steps: [{ text: "Bí quá thì bấm bóng đèn, máy chỉ cho một cột nên thả. Xài gợi ý thì màn đó tối đa 2 sao nha.", focus: "hint" }],
  },
  {
    id: "claw-grips",
    steps: [
      { text: "Từ giờ sức cẩu đổi theo nhịp trên bảng đèn. Mỗi chấm trên đèn là một cột cẩu kéo được, đèn nhiều chấm là lượt chắc tay.", focus: "grips" },
      { text: "Muốn chờ lượt chắc tay thì thả xu vào cột trống. Cẩu gắp hụt chỉ để qua lượt, đôi khi lại là nước cao tay đó." },
    ],
  },
  {
    id: "claw-tall",
    steps: [{ text: "Máy này thấp, mỗi chồng chứa được ít thú. Bạn bị hất văng gặp chồng đầy sẽ lăn tiếp sang trái, có khi lọt thẳng xuống lỗ luôn.", focus: "tall" }],
  },
  {
    id: "claw-heavy",
    steps: [
      { text: "Bạn thú đeo quả tạ là thú nặng ký. Cẩu chở bạn ấy ít hơn một cột, lượt yếu tay chỉ nhấc lên rồi thả lại chỗ cũ.", focus: "heavy" },
      { text: "Thú khác rơi trúng cũng không hất nổi bạn nặng ký đâu, nó chỉ nằm chồng lên trên thôi.", focus: "heavy" },
    ],
  },
  {
    id: "claw-ribbon",
    steps: [{ text: "Hai bạn buộc chung chiếc nơ luôn đi cùng nhau. Gắp bạn trên là bạn dưới theo cùng, bị hất cũng bay cả đôi.", focus: "ribbon" }],
  },
  {
    id: "claw-glass",
    steps: [
      { text: "Vách kính treo chặn đường cẩu. Cẩu chở thú tới vách là va vào, tuột tay ngay trước vách.", focus: "glass" },
      { text: "Nhưng thú bị hất văng thì bay lọt dưới vách. Muốn đưa bạn thú qua vách thì hất qua nha.", focus: "glass" },
    ],
  },
  {
    id: "claw-bouncy",
    steps: [{ text: "Bóng sọc là bóng nảy. Rơi xuống là nảy thêm một cột, bị hất thì bay luôn hai cột.", focus: "bouncy" }],
  },
  {
    id: "claw-heavy-ribbon",
    steps: [{ text: "Thú nặng gặp nơ ruy băng! Đôi nào có bạn nặng ký thì cả đôi đều nặng, cẩu chở ít hơn một cột và không hất nổi.", focus: "heavy" }],
  },
  {
    id: "claw-glass-bouncy",
    steps: [{ text: "Vách kính gặp bóng nảy! Bóng tuột tay trước vách vẫn nảy lọt dưới vách được đó.", focus: "glass" }],
  },
  {
    id: "claw-finale",
    steps: [{ text: "Máy siêu cấp có đủ mọi bí kíp. Cứ từ từ ngắm trước từng xu, không ai hối đâu.", focus: "machine" }],
  },
];

export const CLAW_TUTORIAL_BY_ID: ReadonlyMap<string, ClawTutorial> = new Map(CLAW_TUTORIALS.map((tutorial) => [tutorial.id, tutorial]));
